import type { ReactNode } from "react";
import { navigate } from "@reach/router";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import type { PlayAction, ResetGameAction } from "../../reducer";
import { isComputerSide } from "../../core/baoside";
import type { MoveSequence, Side, MultiplayerGame, PlayerId } from "../../core/types";
import type { Theme } from "../../theme";
import GameStatePropertiesContext, { type ContextType } from "../providers/GameStateProvider";
import GameSettingsContext from "../providers/GameSettingsProvider";
import { Helmet } from "react-helmet";
import { useEffect } from "react";
import { logPageView } from "../../hooks/use-analytics";

type Props = {
  path?: string;
  moveHistory?: Array<{ action: PlayAction | ResetGameAction; sequence: Array<MoveSequence> }>;
  playerId?: PlayerId;
  game?: MultiplayerGame;
};

const containerStyle = ({ gridUnit, colors }: Theme) => ({
  position: "fixed" as const,
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column" as const,
  background: colors.grey95,
  color: colors.textPrimary,
  zIndex: 1000,
  overflow: "hidden"
});

const headerStyle = ({ gridUnit, colors }: Theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${gridUnit * 5}px`,
  borderBottom: `2px solid ${colors.grey8}`,
  background: "white"
});

const titleStyle = ({ gridUnit }: Theme) => ({
  fontSize: gridUnit * 6,
  fontWeight: "bold",
  margin: 0
});

const contentStyle = ({ gridUnit }: Theme) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  padding: `${gridUnit * 5}px`,
  background: "white",
  overflowY: "auto" as const
});

const tableStyle = ({ colors }: Theme) => ({
  width: "100%",
  textAlign: "center" as const,
  borderCollapse: "collapse" as const
});

const trStyle = ({ gridUnit }: Theme) => ({
  tableLayout: "fixed" as const,
  padding: gridUnit,
  display: "table",
  width: "100%"
});

const theadStyle = (theme: Theme) => ({
  borderBottom: `2px solid ${theme.colors.grey8}`,
  ...trStyle(theme)
});

const tbodyStyle = ({ gridUnit }: Theme) => ({
  display: "block",
  overflowY: "auto" as const
});

const thStyle = ({ gridUnit, fontSize, colors }: Theme) => ({
  padding: `${gridUnit * 2}px`,
  fontSize: fontSize,
  fontWeight: "600",
  color: colors.textPrimary
});

const tdStyle = ({ gridUnit, fontSize, colors }: Theme) => ({
  padding: `${gridUnit * 2}px`,
  fontSize: fontSize,
  color: colors.grey1
});

const History = (props: Props): ReactNode => {
  const { t } = useTranslation();
  const context = useContext<ContextType | undefined>(GameStatePropertiesContext);
  const gameSettingsContext = useContext(GameSettingsContext);
  const gameMode = gameSettingsContext.gameMode;
  const moveHistory = props.moveHistory || context?.properties?.moveHistory || [];
  const playActions: Array<{
    action: PlayAction;
    sequence: Array<MoveSequence>;
  }> = moveHistory.filter(
    (d): d is { action: PlayAction; sequence: Array<MoveSequence> } => d.action.type === "USER_PLAY"
  );

  // Determine player's side for multiplayer mode
  const playerSide: number | null = props.game && props.playerId ? (props.playerId === props.game.host ? 0 : 1) : null;

  useEffect(() => {
    logPageView("Bao | Game history", window.location.href);
  });
  return (
    <div css={containerStyle} data-testid="history-page">
      <Helmet>
        <title>Bawo | Move History</title>
        <meta name="description" content="Game move history" />
      </Helmet>
      <header css={headerStyle}>
        <h1 css={titleStyle}>{t("history.title")}</h1>
        <button css={(theme: Theme) => ({ ...theme.primaryButton })} onClick={() => navigate(-1)}>
          {t("common.back")}
        </button>
      </header>
      <div css={contentStyle}>
        {playActions.length === 0 ? (
          <p
            css={({ gridUnit, colors }: Theme) => ({
              fontSize: gridUnit * 4,
              color: colors.grey3,
              textAlign: "center" as const,
              marginTop: gridUnit * 10
            })}
          >
            {t("history.empty")}
          </p>
        ) : (
          <table css={tableStyle}>
            <thead css={theadStyle}>
              <tr>
                <th css={thStyle}>{t("history.columns.user")}</th>
                <th css={thStyle}>{t("history.columns.moveIndex")}</th>
                <th css={thStyle}>{t("history.columns.moveDirection")}</th>
              </tr>
            </thead>
            <tbody css={tbodyStyle}>
              {playActions.map((d, index) => (
                <MoveRow
                  key={`row_${index}`}
                  user={d.action.side}
                  index={d.action.index}
                  direction={d.action.direction}
                  gameMode={gameMode}
                  playerSide={playerSide}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

type MoveRowProps = {
  user: Side;
  index: number;
  direction: number;
  gameMode: string;
  playerSide: number | null;
};
const MoveRow = (props: MoveRowProps) => {
  const { t } = useTranslation();
  let userLabel: string;

  if (props.gameMode === "multi" && props.playerSide != null) {
    // Multiplayer mode: show Host/Guest
    const isHost = props.user === 0;
    const isCurrentPlayer = props.user === props.playerSide;
    if (isCurrentPlayer) {
      userLabel = isHost ? t("history.hostYou") : t("history.guestYou");
    } else {
      userLabel = isHost ? t("history.host") : t("history.guest");
    }
  } else {
    // Single-player mode: show Bot/You
    userLabel = isComputerSide(props.user) ? t("history.bot") : t("history.you");
  }

  return (
    <tr css={trStyle}>
      <td css={tdStyle}>{userLabel}</td>
      <td css={tdStyle}>{props.index}</td>
      <td css={tdStyle}>{props.direction}</td>
    </tr>
  );
};

export default History;
